import React, { useState, useEffect, useRef } from 'react';
import chatbotService from 'services/chatbotService';
import Swal from 'sweetalert2';
import './Chatbot.css';

export const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [conversationId, setConversationId] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [quickResponses, setQuickResponses] = useState([]);
  const [tooltip, setTooltip] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadActiveConversation();
      loadQuickResponses();
    }
  }, [isOpen]);

  // Auto-resize textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = Math.min(inputRef.current.scrollHeight, 100) + 'px';
    }
  }, [inputMessage]);

  const loadActiveConversation = async () => {
    try {
      const response = await chatbotService.getActiveConversation();
      if (!response.error && response.data.data) {
        const { conversation, messages: historyMessages } = response.data.data;
        setConversationId(conversation.id);
        setMessages(historyMessages.map(msg => ({
          role: msg.role,
          content: msg.content,
          timestamp: msg.created_at
        })));
      } else {
        startNewConversation();
      }
    } catch (error) {
      console.error('Error al cargar conversación:', error);
    }
  };

  const loadQuickResponses = async () => {
    try {
      const response = await chatbotService.getQuickResponses();
      if (!response.error) {
        setQuickResponses(response.data.data);
      }
    } catch (error) {
      console.error('Error al cargar respuestas rápidas:', error);
    }
  };

  const startNewConversation = async () => {
    try {
      const response = await chatbotService.newConversation();
      if (!response.error) {
        const { conversationId: newId, welcomeMessage } = response.data.data;
        setConversationId(newId);
        setMessages([{
          role: 'assistant',
          content: welcomeMessage,
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Error al crear conversación:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo iniciar el chatbot',
        timer: 2000,
        showConfirmButton: false
      });
    }
  };

  const handleSendMessage = async (messageText = null) => {
    const textToSend = messageText || inputMessage.trim();
    if (!textToSend) return;

    const userMessage = {
      role: 'user',
      content: textToSend,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsTyping(true);

    try {
      const response = await chatbotService.sendMessage({
        message: textToSend,
        conversationId
      });

      if (!response.error) {
        const { conversationId: newConvId, message: botMessage } = response.data.data;
        if (!conversationId) setConversationId(newConvId);

        setMessages(prev => [...prev, {
          role: 'assistant',
          content: botMessage,
          timestamp: new Date()
        }]);
      }
    } catch (error) {
      console.error('Error al enviar mensaje:', error);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: '❌ Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta nuevamente.',
        timestamp: new Date(),
        isError: true
      }]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleNewChat = async () => {
    const result = await Swal.fire({
      title: '¿Nueva conversación?',
      text: 'Se cerrará la conversación actual',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Sí, nueva conversación',
      cancelButtonText: 'Cancelar',
      confirmButtonColor: '#1a2332'
    });

    if (result.isConfirmed) {
      setMessages([]);
      setConversationId(null);
      await startNewConversation();
    }
  };

  const toggleChatbot = () => setIsOpen(!isOpen);

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  };

  // Map icon names from data to Bootstrap Icons classes
  const getIconClass = (iconValue) => {
    const iconMap = {
      '📦': 'bi-box-seam',
      '🚚': 'bi-truck',
      '💳': 'bi-credit-card',
      '🔄': 'bi-arrow-repeat',
      '📞': 'bi-telephone',
      '❓': 'bi-question-circle',
      '📋': 'bi-clipboard-list',
      '⚙️': 'bi-gear',
      '🏠': 'bi-house',
      '📊': 'bi-bar-chart',
    };
    return iconMap[iconValue] || 'bi-chat-dots';
  };

  return (
    <>
      {/* Floating button */}
      <button
        className={`chatbot-toggle ${isOpen ? 'open' : ''}`}
        onClick={toggleChatbot}
        title={isOpen ? 'Cerrar asistente' : 'Abrir asistente virtual'}
      >
        <i className={`bi ${isOpen ? 'bi-x-lg' : 'bi-headset'}`}></i>
        {!isOpen && <span className="toggle-badge"></span>}
      </button>

      {isOpen && (
        <div className="chatbot-window">

          {/* Header */}
          <div className="chatbot-header">
            <div className="chatbot-header-info">
              <div className="chatbot-avatar-wrap">
                 <div className="chatbot-avatar bot">
    <img
            src="/img/chatbot.png"
            height="46"
            alt="Chatbot Logo"
          />
</div>
                <span className="avatar-status-dot"></span>
              </div>
              <div>
                <h3 className="chatbot-title">Sonia</h3>
                <p className="chatbot-status">En línea · Responde al instante</p>
              </div>
            </div>
            <div className="chatbot-header-actions">
              <div className="tooltip-wrap">
                <button className="btn-icon" onClick={handleNewChat}>
                  <i className="bi bi-plus-circle"></i>
                </button>
                <span className="tooltip-text">Nueva conversación</span>
              </div>
              <div className="tooltip-wrap">
                <button className="btn-icon" onClick={toggleChatbot}>
                  <i className="bi bi-dash-lg"></i>
                </button>
                <span className="tooltip-text">Minimizar</span>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="chatbot-messages">
            {messages.length === 0 ? (
              <div className="chatbot-empty">
                <div className="empty-icon-wrap">
                  <i className="bi bi-chat-square-dots"></i>
                </div>
                <p className="empty-title">Iniciando conversación...</p>
                <p className="empty-sub">Un momento por favor</p>
              </div>
            ) : (
              messages.map((msg, index) => (
                <div key={index} className={`message ${msg.role} ${msg.isError ? 'error' : ''}`}>
                  {msg.role === 'assistant' && (
                    <div className="message-avatar">
                       <img
            src="/img/chatbot.png"
            height="25"
            alt="Chatbot Logo"
          />
                    </div>
                  )}
                  <div className="message-bubble">
                    <p className="message-text">{msg.content}</p>
                    <span className="message-time">
                      <i className="bi bi-clock"></i> {formatTime(msg.timestamp)}
                    </span>
                  </div>
                  {msg.role === 'user' && (
                    <div className="message-avatar user-avatar-icon">
                      <i className="bi bi-person"></i>
                    </div>
                  )}
                </div>
              ))
            )}

            {isTyping && (
              <div className="message assistant">
                <div className="message-avatar">
                  <img
            src="/img/chatbot.png"
            height="25"
            alt="Chatbot Logo"
          />
                </div>
                <div className="typing-indicator">
                  <span></span><span></span><span></span>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          {/* Quick responses as icon action buttons with tooltips */}
          {messages.length > 0 && !isTyping && quickResponses.length > 0 && (
            <div className="chatbot-quick-responses">
              <p className="quick-label">
                <i className="bi bi-lightning-charge-fill"></i> Acciones rápidas
              </p>
              <div className="quick-actions-row">
                {quickResponses.slice(0, 6).map((qr) => (
                  <div className="tooltip-wrap" key={qr.id}>
                    <button
                      className="quick-action-btn"
                      onClick={() => handleSendMessage(qr.question)}
                    >
                      <i className={`bi ${getIconClass(qr.icon)}`}></i>
                    </button>
                    <span className="tooltip-text tooltip-top">{qr.question}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="chatbot-input-container">
            <div className="input-wrap">
              <textarea
                ref={inputRef}
                className="chatbot-input"
                placeholder="Escribe tu mensaje..."
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                rows="1"
                disabled={isTyping}
              />
              <div className="input-hint">
                <i className="bi bi-shift"></i> Shift+Enter para nueva línea
              </div>
            </div>
            <button
              className="btn-send"
              onClick={() => handleSendMessage()}
              disabled={!inputMessage.trim() || isTyping}
              title="Enviar mensaje"
            >
              <i className="bi bi-send-fill"></i>
            </button>
          </div>

        </div>
      )}
    </>
  );
};